import type { Locale } from '@/i18n/locales'

import { DISPLAY_CURRENCIES, LOCALE_DEFAULT_CURRENCY, type DisplayCurrency } from './map'
import { CURRENCY_PARAM } from './resolve'

/**
 * The address that renders the page a reader is on in `currency`.
 *
 * The locale's own default names the **param-free** URL, because a `cur` equal to it is
 * redundant and redirects — an option offering the reader their default has to name the
 * canonical page directly, or every return to it costs a hop. Every other currency
 * replaces whatever the address carried. The rest of the query survives either way: on
 * the travel review it *is* the page.
 *
 * Distinct from `hrefWithDisplayCurrency`, which carries **the page's own** selection
 * onto a link to somewhere else. This builds a link to the *same* page in a **named**
 * currency, so it sets rather than appends, and it cannot decline.
 */
export function currencyHref(
    pathname: string,
    search: string,
    currency: DisplayCurrency,
    locale: Locale,
): string {
    const params = new URLSearchParams(search)

    if (currency === LOCALE_DEFAULT_CURRENCY[locale]) {
        params.delete(CURRENCY_PARAM)
    } else {
        params.set(CURRENCY_PARAM, currency)
    }

    const query = params.toString()
    return query ? `${pathname}?${query}` : pathname
}

/**
 * A currency's symbol as the active locale writes it — `$` for `USD` on `en-us`, `MX$`
 * for `MXN` there, and the reverse on `es-mx`.
 *
 * Read out of the same ICU data every rendered price is formatted from, so a control that
 * names a currency and a price stated in it cannot disagree about what it looks like.
 * `Intl` directly rather than the shared formatter only because extracting a symbol is not
 * formatting a number, and that formatter exposes no parts API; it is the same constructor
 * underneath.
 *
 * `formatToParts` rather than trimming digits out of a formatted string: the currency part
 * is the symbol, whatever position, spacing or numbering system the locale puts around it.
 * Falls back to the code, which is what a locale without a symbol renders anyway.
 */
export function currencySymbol(locale: Locale, currency: DisplayCurrency): string {
    const parts = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
    }).formatToParts(0)

    return parts.find((part) => part.type === 'currency')?.value ?? currency
}

/**
 * A currency's name as the active locale writes it, from the same CLDR data every rendered
 * price is formatted from — a name is the locale's fact, not copy to translate. CLDR
 * lower-cases it in the Romance languages ("dólar estadounidense"); a menu row is a label,
 * so its first letter is upper-cased by the locale's own rule.
 */
export function currencyName(locale: Locale, currency: DisplayCurrency): string {
    const name = new Intl.DisplayNames(locale, { type: 'currency' }).of(currency) ?? currency
    return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1)
}

export type CurrencyLabels = Readonly<
    Record<DisplayCurrency, { readonly name: string; readonly symbol: string }>
>

/**
 * Every displayable currency's name and symbol in `locale`, for the server to render and
 * hand the picker. The browser's locale data can differ from the server's, so a picker
 * formatting its own labels during hydration could disagree with the markup it hydrates.
 */
export function currencyLabels(locale: Locale): CurrencyLabels {
    return Object.fromEntries(
        DISPLAY_CURRENCIES.map((currency) => [
            currency,
            { name: currencyName(locale, currency), symbol: currencySymbol(locale, currency) },
        ]),
    ) as Record<DisplayCurrency, { name: string; symbol: string }>
}
